import { useCallback, useRef, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import QrScanner from "@/components/events/QrScanner";
import { useEvent } from "@/lib/events/useEvent";
import { decodeTicket } from "@/lib/events/ticket";
import { trackEvent } from "@/lib/analytics/track";
import type { AppId } from "@/lib/apps";

const isAppId = (s: string): s is AppId =>
  s === "fryqnc" || s === "pulsynq" || s === "mindlynq";

interface ScanFeedback {
  kind: "ok" | "dup" | "wrong_event" | "invalid" | "error" | "unknown_guest";
  guestId?: string;
  guestName?: string | null;
  message: string;
  at: number;
}

const EventCheckInInner = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { event, isHost, allGuests, loading, setGuestCheckedIn } = useEvent(eventId);

  const [feedback, setFeedback] = useState<ScanFeedback | null>(null);
  const lastHandled = useRef<{ text: string; at: number } | null>(null);

  const handleDecode = useCallback(
    async (text: string) => {
      // Debounce identical scans within 1.5s.
      const now = Date.now();
      if (lastHandled.current && lastHandled.current.text === text && now - lastHandled.current.at < 1500) {
        return;
      }
      lastHandled.current = { text, at: now };

      const payload = decodeTicket(text);
      if (!payload) {
        setFeedback({
          kind: "invalid",
          message: "Not a valid ticket QR.",
          at: now,
        });
        return;
      }
      if (!event || payload.e !== event.id) {
        setFeedback({
          kind: "wrong_event",
          message: "This ticket is for a different event.",
          at: now,
        });
        return;
      }
      const guest = allGuests.find((g) => g.id === payload.g);
      if (!guest) {
        setFeedback({
          kind: "unknown_guest",
          message: "Ticket not found in guest list.",
          at: now,
        });
        return;
      }
      if (guest.checked_in) {
        setFeedback({
          kind: "dup",
          guestId: guest.id,
          guestName: guest.guest_name,
          message: `${guest.guest_name ?? "Attendee"} is already checked in.`,
          at: now,
        });
        return;
      }
      const res = await setGuestCheckedIn(guest.id, true);
      if ("error" in res && res.error) {
        setFeedback({ kind: "error", message: res.error, at: now });
        return;
      }
      setFeedback({
        kind: "ok",
        guestId: guest.id,
        guestName: guest.guest_name,
        message: `Checked in ${guest.guest_name ?? "attendee"}.`,
        at: now,
      });
      trackEvent("event_checkin", {
        app: event && isAppId(event.app) ? event.app : null,
        props: {
          event_id: event?.id ?? null,
          guest_id: guest.id,
          source: "scanner",
        },
      });
    },
    [event, allGuests, setGuestCheckedIn],
  );

  if (loading) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-12 text-sm text-muted-foreground">
        Loading…
      </main>
    );
  }
  if (!event) return <Navigate to="/events" replace />;
  if (!isHost) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-12">
        <h1 className="text-2xl font-semibold">Hosts only</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Only the event host can run check-in.
        </p>
        <Link
          to={`/events/${event.id}`}
          className="mt-4 inline-flex text-xs text-muted-foreground hover:text-foreground"
        >
          ← Back to event
        </Link>
      </main>
    );
  }

  const checkedIn = allGuests.filter((g) => g.checked_in).length;
  const tone =
    feedback?.kind === "ok"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
      : feedback?.kind === "dup"
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30"
        : "bg-destructive/15 text-destructive border-destructive/30";

  return (
    <main className="mx-auto max-w-2xl px-6 py-10">
      <header className="mb-6">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          Check-in
        </p>
        <h1 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">
          {event.title}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {checkedIn} of {allGuests.length} checked in
        </p>
      </header>

      <QrScanner onDecode={handleDecode} />

      {feedback ? (
        <div
          key={feedback.at}
          className={`mt-4 rounded-xl border p-4 text-sm ${tone}`}
        >
          {feedback.message}
        </div>
      ) : null}

      <section className="mt-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Manual check-in
        </h2>
        <ul className="mt-3 divide-y divide-border rounded-lg border border-border">
          {allGuests.length === 0 ? (
            <li className="p-3 text-sm text-muted-foreground">No guests yet.</li>
          ) : (
            allGuests.map((g) => (
              <li
                key={g.id}
                className="flex items-center justify-between gap-3 p-3 text-sm"
              >
                <span>{g.guest_name ?? "Primary attendee"}</span>
                {g.checked_in ? (
                  <button
                    type="button"
                    onClick={() => setGuestCheckedIn(g.id, false)}
                    className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25"
                  >
                    Checked in · undo
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      const res = await setGuestCheckedIn(g.id, true);
                      if (!("error" in res)) {
                        trackEvent("event_checkin", {
                          app: event && isAppId(event.app) ? event.app : null,
                          props: {
                            event_id: event?.id ?? null,
                            guest_id: g.id,
                            source: "manual",
                          },
                        });
                      }
                    }}
                    className="rounded-full bg-foreground text-background px-3 py-1 text-xs font-medium"
                  >
                    Check in
                  </button>
                )}
              </li>
            ))
          )}
        </ul>
      </section>

      <Link
        to={`/events/${event.id}`}
        className="mt-8 inline-flex text-xs text-muted-foreground hover:text-foreground"
      >
        ← Back to event
      </Link>
    </main>
  );
};

const EventCheckIn = () => (
  <div className="min-h-screen flex flex-col bg-background text-foreground">
    <SiteHeader />
    <OnboardingGate>
      <EventCheckInInner />
    </OnboardingGate>
    <SiteFooter />
  </div>
);

export default EventCheckIn;
